<template>
  <div
    id="data_extraction_tab"
    class="flex flex-col gap-6 proxima_nova max-w-4xl mx-auto w-full h-full overflow-y-auto"
  >
    <VoiceAgentSectionHeader
      :title="tabHeaders.dataExtraction.title"
      :description="tabHeaders.dataExtraction.description"
    />

    <div
      v-if="properties.length === 0 && !selectedPropertyType"
      id="data_extraction_empty_state"
    >
      <CustomContainer :showAddButton="false">
        <div
          class="flex flex-col items-center justify-center gap-6 min-h-[320px]"
        >
          <div class="flex flex-col items-center gap-2">
            <ThemeImage
              :icon="imageNames.EMPTY_SCREEN_GREY"
              alt="data_extraction_icon"
              class="size-16"
            />
            <span class="text-base font-semibold">Data Extraction</span>
            <span class="text-sm secondary_color text-center">
              Add the first data property your agent will capture during calls
            </span>
          </div>
          <AddItemDropdown
            button-text="Add"
            :menu-items="propertyMenuItems"
            :show-description="true"
            @select="handleAddProperty"
          />
        </div>
      </CustomContainer>
    </div>

    <div
      v-else-if="selectedPropertyType"
      id="data_extraction_form_container"
      class="flex flex-col gap-4"
    >
      <DataExtractionForm
        :config="selectedPropertyConfig"
        :property-data="editingProperty"
        :is-edit="editingIndex !== null"
        :agent-id="formData.agent_id"
        @save="handleSaveEdit"
        @delete="handleDeleteFromEdit"
      />
    </div>

    <div
      v-else-if="properties.length > 0"
      id="data_extraction_properties_list"
      class="flex flex-col gap-4"
    >
      <VoiceAgentItemCard
        v-for="(property, index) in properties"
        :key="`property_${property.id || index}`"
        :icon="getPropertyIcon(property.type)"
        icon-alt="property_icon"
        :title="property.name"
        :subtitle="property.extraction"
        :date="property.created_at"
        @edit="handleEditProperty(index)"
        @delete="handleDeleteProperty(index)"
      />

      <div class="flex justify-center">
        <AddItemDropdown
          button-text="Add"
          :menu-items="propertyMenuItems"
          :show-description="true"
          @select="handleAddProperty"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import VoiceAgentSectionHeader from "@/src/components/voice_agents/VoiceAgentSectionHeader.vue";
import DataExtractionForm from "@/src/components/voice_agents/data_extraction/DataExtractionForm.vue";
import VoiceAgentItemCard from "@/src/components/voice_agents/VoiceAgentItemCard.vue";
import AddItemDropdown from "@/src/components/voice_agents/AddItemDropdown.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  dataExtractionMenuItems,
  dataExtractionTypes,
  tabHeaders,
} from "@/src/constants/voiceAgentConstants.js";

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:formData"]);
const selectedPropertyType = ref(null);
const selectedPropertyConfig = ref(null);
const editingIndex = ref(null);
const editingProperty = ref(null);

const properties = computed(
  () => props.formData.data_extraction_properties || []
);

const getPropertyIcon = computed(() => (type) => {
  const config = Object.values(dataExtractionTypes).find(
    (item) => item.type === type
  );
  return config ? config.icon : imageNames.PROPERTY_TYPE_TEXT;
});

const closeForm = () => {
  try {
    selectedPropertyType.value = null;
    selectedPropertyConfig.value = null;
    editingIndex.value = null;
    editingProperty.value = null;
  } catch (error) {
    console.error("Error closing form:", error);
  }
};

const openForm = (config, property = null, index = null) => {
  try {
    selectedPropertyType.value = config.type;
    selectedPropertyConfig.value = config;
    editingIndex.value = index;
    editingProperty.value = property ? { ...property } : null;
  } catch (error) {
    console.error("Error opening form:", error);
  }
};

const updateProperties = (updatedProperties) => {
  try {
    emit("update:formData", {
      ...props.formData,
      data_extraction_properties: updatedProperties,
    });
  } catch (error) {
    console.error("Error updating properties:", error);
  }
};

const handleAddProperty = (config) => {
  try {
    openForm(config);
  } catch (error) {
    console.error("Error adding property:", error);
  }
};

const handleEditProperty = (index) => {
  try {
    const property = properties.value[index];
    const config = Object.values(dataExtractionTypes).find(
      (item) => item.type === property.type
    );
    openForm(config, property, index);
  } catch (error) {
    console.error("Error editing property:", error);
  }
};

const handleSaveEdit = (propertyData) => {
  try {
    const updatedProperties = [...properties.value];

    if (editingIndex.value !== null) {
      updatedProperties[editingIndex.value] = {
        ...propertyData,
        created_at:
          updatedProperties[editingIndex.value]?.created_at ||
          propertyData.created_at,
      };
    } else {
      updatedProperties.push(propertyData);
    }

    updateProperties(updatedProperties);
    closeForm();
  } catch (error) {
    console.error("Error saving property:", error);
  }
};

const handleDeleteFromEdit = () => {
  try {
    if (editingIndex.value !== null) {
      handleDeleteProperty(editingIndex.value);
    }
    closeForm();
  } catch (error) {
    console.error("Error deleting from edit:", error);
  }
};

const handleDeleteProperty = (index) => {
  try {
    const updatedProperties = [...properties.value];
    updatedProperties.splice(index, 1);
    updateProperties(updatedProperties);
  } catch (error) {
    console.error("Error deleting property:", error);
  }
};

const handleContinue = async () => {
  try {
    return true;
  } catch (error) {
    console.error("Error in data extraction tab:", error);
    return false;
  }
};

const propertyMenuItems = ref(dataExtractionMenuItems);

defineExpose({
  handleContinue,
});
</script>
